import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Book, Layout, CreditCard } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { auth } from './firebase/config';
import { API_URL } from './config';
import { Button } from './components/ui/button';
import LogoutButton from './components/LogoutButton';

const SubscriptionsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();

    // NavItem component for side navigation
    const NavItem = ({ icon, label, path, active }) => (
        <button
            onClick={() => navigate(path)}
            className={`p-3 rounded-xl transition-colors ${active ? 'bg-purple-600/20 text-purple-400' : 'text-gray-400 hover:bg-purple-600/10'
                }`}
            title={label}
        >
            {icon}
        </button>
    );

    const { data: subscriptionData, isLoading } = useQuery({
        queryKey: ['subscription', auth.currentUser?.uid],
        queryFn: async () => {
            const token = await auth.currentUser?.getIdToken();
            if (!token) return null;

            const response = await fetch(`${API_URL}/subscription`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 401) {
                navigate('/');
                return null;
            }

            if (!response.ok) {
                throw new Error('Failed to fetch subscription data');
            }

            return response.json();
        },
        enabled: !!auth.currentUser,
        staleTime: 5 * 60 * 1000,
    });

    const handlePayment = (priceId) => {
        if (window.Paddle) {
            window.Paddle.Checkout.open({
                items: [{
                    priceId: priceId,
                    quantity: 1
                }]
            });
        }
    };

    const plans = [
        {
            id: 'free',
            name: 'Free',
            price: '$0',
            features: [
                'First 3 papers',
                'Access to all mindmaps',
                'Unlimited AI chat',
            ]
        },
        {
            id: 'pro',
            name: 'Pro',
            priceId: process.env.REACT_APP_PADDLE_PRO_PRICE_ID,
            price: '$10/month',
            featured: false,
            features: [
                'Everything in Free',
                '10 papers per month',
                'Priority support',
            ]
        },
    ];

    const handleCheckoutCompleted = async (checkoutData) => {
        try {
            console.log('Handling checkout completion:', checkoutData);
            const token = await auth.currentUser?.getIdToken();
            if (!token) {
                console.error('No auth token available');
                navigate('/');
                return;
            }

            const response = await fetch(`${API_URL}/subscription/checkout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    customerId: checkoutData.customerId,
                })
            });

            if (!response.ok) {
                throw new Error(`API call failed: ${response.status}`);
            }

            // Invalidate the subscription query to refresh the data
            queryClient.invalidateQueries(['subscription', auth.currentUser?.uid]);

            console.log('Subscription updated successfully');
        } catch (error) {
            console.error('Error updating subscription:', error);
        }
    };

    useEffect(() => {
        if (window.Paddle) {
            if (process.env.REACT_APP_PADDLE_ENVIRONMENT === 'sandbox') {
                window.Paddle.Environment.set("sandbox");
            }
            window.Paddle.Initialize({
                token: process.env.REACT_APP_PADDLE_CLIENT_ID,
                eventCallback: (event) => {
                    console.log('Paddle event:', event.name, event);

                    // Handle both transaction and subscription completed events
                    if (event.name === 'checkout.completed' || event.name === 'subscription.created') {
                        const checkoutData = {
                            customerId: event.data?.customer?.id,
                        };

                        handleCheckoutCompleted(checkoutData);
                    }
                }
            });
        }
    }, []);

    return (
        <div className="flex h-screen bg-black text-white">
            {/* Left Sidebar - Main Navigation */}
            <div className="w-16 border-r border-purple-900/30 flex flex-col items-center py-4">
                <div className="flex-1 flex flex-col items-center space-y-6">
                    <NavItem icon={<Search />} label="Search" path="/search" active={location.pathname === '/search'} />
                    <NavItem icon={<Layout />} label="Workspace" path="/workspace" active={location.pathname === '/workspace'} />
                    <NavItem icon={<Book />} label="Library" path="/library" active={location.pathname === '/library'} />
                    <NavItem icon={<CreditCard />} label="Subscription" path="/subscription" active={location.pathname === '/subscription'} />
                </div>
                <LogoutButton />
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Header */}
                <header className="h-16 border-b border-purple-900/30 flex items-center justify-between px-6">
                    <h1 className="text-2xl font-semibold text-purple-400">Subscription</h1>
                </header>

                {/* Scrollable Content */}
                <main className="flex-1 overflow-auto p-6">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="flex flex-col items-center gap-4">
                                <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                                <p className="text-gray-400">Loading subscription details...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="max-w-6xl mx-auto">
                            {/* Current Plan Status */}
                            <div className="mb-8 p-6 rounded-lg border border-purple-900/30 bg-purple-900/10">
                                <h2 className="text-xl font-semibold text-purple-400 mb-4">Current Plan</h2>
                                <div className="grid gap-4">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="text-lg text-white">
                                                {subscriptionData?.currentPlan || 'Free Plan'}
                                            </p>
                                            <p className="text-sm text-gray-400">
                                                Renewal date: {subscriptionData?.renewalDate || 'N/A'}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm text-gray-400">Usage this month</p>
                                            <p className="text-lg text-white">
                                                {subscriptionData?.usage?.papers || 0} papers
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Available Plans */}
                            <h2 className="text-xl font-semibold text-purple-400 mb-6">Available Plans</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                                {plans.map((plan) => (
                                    <div
                                        key={plan.id}
                                        className={`relative p-8 rounded-2xl border ${plan.featured
                                            ? 'border-purple-500 bg-gradient-to-b from-purple-900/40 to-purple-900/20'
                                            : 'border-purple-900/30 bg-purple-900/10'
                                            } flex flex-col transform transition-all duration-200 hover:scale-105`}
                                    >
                                        {plan.featured && (
                                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                                <span className="bg-purple-500 text-white text-xs font-semibold px-4 py-1 rounded-full">
                                                    MOST POPULAR
                                                </span>
                                            </div>
                                        )}
                                        <div className="mb-6">
                                            <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                                            <div className="flex items-baseline">
                                                <span className="text-4xl font-bold text-purple-400">{plan.price}</span>
                                                {/* <span className="text-gray-400 ml-2">{plan.price}</span> */}
                                            </div>
                                        </div>
                                        <ul className="flex-1 space-y-4 mb-8">
                                            {plan.features.map((feature, index) => (
                                                <li key={index} className="flex items-center text-gray-300">
                                                    <span className="mr-3 text-purple-400">
                                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                        </svg>
                                                    </span>
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                        <Button
                                            onClick={() => plan.id === 'free' ? navigate('/library') : handlePayment(plan.priceId)}
                                            className={`w-full py-4 text-base font-semibold transition-all duration-200 rounded-xl shadow-lg ${subscriptionData?.currentPlan === plan.name
                                                ? 'bg-gray-600/50 cursor-not-allowed'
                                                : plan.featured
                                                    ? 'bg-purple-500 hover:bg-purple-600 hover:shadow-purple-500/30 hover:-translate-y-0.5'
                                                    : 'bg-purple-600/30 hover:bg-purple-600/50 hover:shadow-purple-600/20 hover:-translate-y-0.5'
                                                }`}
                                            disabled={subscriptionData?.currentPlan === plan.name}
                                        >
                                            {subscriptionData?.currentPlan === plan.name ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                    Current Plan
                                                </span>
                                            ) : (
                                                plan.id === 'free' ? 'Go to Library' : 'Get Started'
                                            )}
                                        </Button>
                                    </div>
                                ))}
                            </div>

                            {/* Support Email Section */}
                            <div className="mt-12 text-center text-gray-400">
                                <p>Need more credits or have questions? Contact us at{' '}
                                    <a
                                        href="mailto:support@paperswithgraph.com"
                                        className="text-purple-400 hover:text-purple-300 transition-colors"
                                    >
                                        support@paperswithgraph.com
                                    </a>
                                </p>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default SubscriptionsPage; 